import axios from 'axios'
import { storage } from '@/utils/storage'

// Vuex module: auth.
// Handles the three OTP flows (customer / vendor / technology) and exposes
// branchname/coname/cocode for the AppShell to inject as props into the
// legacy components.

export default {
  namespaced: true,

  state: () => ({
    token: storage.getToken(),
    userid: storage.getUserId(),
    role: storage.getRole(),
    session: storage.getSession(),
    loading: false,
  }),

  getters: {
    isAuthenticated: (s) => !!(s.token || s.userid || (s.session && Object.keys(s.session).length > 0)),
    coname: (s) => s.session?.coname || s.session?.Description || '',
    branchname: (s) => s.session?.branchname || s.session?.branch_name || '',
    branchcode: (s) => s.session?.branchcode || s.session?.Branch_Code || '',
    cocode: (s) => s.session?.cocode || s.session?.Code || '',
    loc: (s) => s.session?.loc || s.session?.branchcode || s.session?.Branch_Code || '',
    abr: (s) => s.session?.abr || '',
    branches: (s) => s.session?.branches || [],
  },

  mutations: {
    SET_LOADING(state, v) { state.loading = v },
    SET_TOKEN(state, t) {
      state.token = t
      if (t) storage.setToken(t); else storage.clearToken()
    },
    SET_USERID(state, id) {
      state.userid = id !== null && id !== undefined ? String(id) : null
      if (state.userid) storage.setUserId(state.userid); else storage.clearUserId()
    },
    SET_ROLE(state, r) {
      state.role = r
      if (r) storage.setRole(r); else storage.clearRole()
    },
    SET_SESSION(state, s) {
      state.session = s
      if (s) storage.setSession(s); else storage.clearSession()
    },
    MERGE_SESSION(state, patch) {
      const next = { ...(state.session || {}), ...(patch || {}) }
      state.session = next
      storage.setSession(next)
    },
    RESET(state) {
      state.token = null
      state.userid = null
      state.role = null
      state.session = null
      storage.clearAll()
    },
  },

  actions: {
    async sendOtp({ commit }, { mobile, scope = 'user' }) {
      commit('SET_LOADING', true)
      try {
        const url =
          scope === 'vendor' ? '/api/vendor-send-otp'
          : scope === 'technology' ? '/api/technology-send-otp'
          : '/api/send-otp'
        return await axios.get(url, { params: { mobile } })
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async verifyOtp({ commit }, { mobile, otp, scope = 'user' }) {
      commit('SET_LOADING', true)
      try {
        if (scope === 'user') {
          const { data } = await axios.get('/api/verify-otp', { params: { mobile, otp } })
          if (!data?.token) throw new Error(data?.message || 'OTP verification failed')
          commit('SET_TOKEN', data.token)
          commit('SET_USERID', data.userid)
          commit('SET_ROLE', 'user')
          commit('SET_SESSION', { id: data.userid, mobile })
          return data
        }
        const url = scope === 'vendor' ? '/api/vendor-verify-otp' : '/api/technology-verify-otp'
        const { data } = await axios.get(url, { params: { mobile, otp } })
        if (!data?.success) throw new Error(data?.message || 'OTP verification failed')
        const payload = data.data || {}
        // Vendor/Technology endpoints take `userid` query param; the live mobile
        // apps use `data.code` for that lookup key.
        const id = payload.userid || payload.code
        commit('SET_TOKEN', null)
        commit('SET_USERID', id)
        commit('SET_ROLE', scope)
        commit('SET_SESSION', { ...payload, mobile, role: scope })
        return data
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Step 1 — submit username + password, receive OTP on mobile
    async login({ commit }, { username, password, financial_year }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await axios.post('/api/admin/login', { username, password, financial_year })
        if (!data?.success && !data?.otp_required && !data?.userid) {
          throw new Error(data?.message || data?.error || 'Login failed')
        }
        // Return to component; token is not set yet — OTP must be verified first
        return data
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // TEMP (Play Store review): finish the login straight from the
    // credentials response, WITHOUT the OTP step — the review team can't
    // receive OTPs, so the app was rejected. Nothing of the OTP flow is
    // removed; LoginPage.vue's SKIP_OTP flag routes here instead of the OTP
    // screen and can be flipped back at any time.
    async adoptLoginSession({ commit }, { data, financial_year }) {
      // Prime the Laravel web session cookie like verifyAdminOtp does, so the
      // legacy /admin/* (session-authenticated) routes keep working.
      await axios.get('/sanctum/csrf-cookie').catch(() => {})
      if (data?.token) commit('SET_TOKEN', data.token)
      commit('SET_USERID', data?.userid)
      commit('SET_ROLE', data?.role || 'admin')
      commit('SET_SESSION', { ...(data?.session || data || {}), financial_year })
    },

    // Step 2 — verify OTP and receive the auth token.
    // Also primes the Laravel web session so legacy /admin/* routes work.
    async verifyAdminOtp({ commit }, { userid, otp, financial_year, branch_code }) {
      commit('SET_LOADING', true)
      try {
        // Prime the Sanctum web session before token auth so the server can
        // set its laravel_session cookie (needed by legacy /admin/* controllers).
        await axios.get('/sanctum/csrf-cookie').catch(() => {})

        const { data } = await axios.post('/api/admin/verify-otp', {
          userid, otp, financial_year, branch_code,
        })
        if (!data?.success || !data?.token) {
          throw new Error(data?.message || data?.error || 'OTP verification failed')
        }
        commit('SET_TOKEN', data.token)
        commit('SET_USERID', data.userid)
        commit('SET_ROLE', data.role || 'admin')
        commit('SET_SESSION', { ...(data.session || data), financial_year })
        return data
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Resend OTP
    async resendAdminOtp({ commit }, { userid, financial_year }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await axios.post('/api/admin/resend-otp', { userid, financial_year })
        if (!data?.success) throw new Error(data?.message || 'Failed to resend OTP')
        return data
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async logout({ commit, state }) {
      try {
        if (state.token) await axios.post('/api/logout')
      } catch {
        // swallow — local state wins on signout
      } finally {
        commit('RESET')
      }
    },
  },
}
